import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "@components/Button/Button";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormattedReference } from "@hooks/useFormattedReference";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { getBerthVisitedCodes } from "@services/deficienciesApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Company } from "@services/vessel-call/types";
import type { Vessel } from "@services/vessel/types";
import { getSearchVessels } from "@services/vesselsApi";
import { useQuery } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CREATE_DEFICIENCY_FIELDS as F,
  FOTT_BERTH_FROM_TO_LOCATION,
  MAX_DEFICIENCY_ATTACHMENTS,
  MAX_DEFICIENCY_DETAILS_LENGTH,
  MAX_DEFICIENCY_IMO_DIGITS,
  MAX_DEFICIENCY_REMARKS_LENGTH,
  MAX_TRIP_NO_PLDA_LENGTH,
} from "./constants";
import { useCreateDeficiencyRequest } from "./hooks/useCreateDeficiencyRequest";
import { mapRegisteredVesselToDeficiencyFormValues } from "./mappers";
import {
  createDeficiencyDefaultValues,
  getCreateDeficiencyFormSchema,
  type CreateDeficiencyFormData,
} from "./schema";

const TEST_ID_BASE = "create-deficiency-page";
const LANG_BASE = "deficiencies";
const VESSEL_SERVICE = "bff-vessel";

const isMatchingImoNumber = (vessel: Vessel, imoNumber: string) => {
  return String(vessel.vesselImoNumber ?? "") === imoNumber;
};

const CreateDeficiencyPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { userActionPermissions } = useAuthStore();
  const { additionalDocuments: attachmentUploadConditions } = useFileUploadProfiles();
  const [resolvedImoNumber, setResolvedImoNumber] = useState<string | null>(null);
  const [isLookingUpVessel, setIsLookingUpVessel] = useState(false);
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const canRequestFottDeficiency = checkPermission(
    userActionPermissions,
    "canRequestFottDeficiency",
  );
  const lookupRequestIdRef = useRef(0);

  useEffect(() => {
    if (!canRequestFottDeficiency) {
      navigate(`/${paths.deficiencies.index}`, { replace: true });
    }
  }, [canRequestFottDeficiency, navigate]);

  const schema = useMemo(() => getCreateDeficiencyFormSchema(t), [t]);

  const { data: berthVisitedCodes = [], isLoading: isLoadingBerthVisitedCodes } = useQuery({
    queryKey: ["deficiency-berth-visited-codes", FOTT_BERTH_FROM_TO_LOCATION],
    queryFn: () => getBerthVisitedCodes(FOTT_BERTH_FROM_TO_LOCATION),
    enabled: canRequestFottDeficiency,
  });
  const { options: companies = [] } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const berthVisitedOptions = useMemo(
    () =>
      berthVisitedCodes.map(({ berthCode, berthName }) => ({
        value: berthCode,
        label: berthName,
      })),
    [berthVisitedCodes],
  );

  const form = useForm<CreateDeficiencyFormData>({
    resolver: zodResolver(schema),
    defaultValues: createDeficiencyDefaultValues,
    mode: "onBlur",
  });

  const { clearErrors, getValues, reset, setError, setValue, watch } = form;
  const currentImoNumber = watch(F.IMO_NO);

  const allowedAttachmentExtensions = useMemo(
    () =>
      (attachmentUploadConditions.format ?? []).map((extension) =>
        extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
      ),
    [attachmentUploadConditions.format],
  );

  const {
    attachmentFiles,
    setAttachmentFiles,
    handleAttachmentUpload,
    resetAttachmentUpload,
    isUploadingAttachments,
    isSubmitting,
    submitRequest,
  } = useCreateDeficiencyRequest();

  const resetRegisteredVesselFields = useCallback(() => {
    setValue(F.REGISTERED_VESSEL_ID, "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    setValue(F.VESSEL_NAME, "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    setValue(F.SHIPPING_AGENCY, "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    setValue(F.LOA, "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [setValue]);

  const clearDeficiencyForm = useCallback(() => {
    reset(createDeficiencyDefaultValues);
    resetAttachmentUpload();
    setResolvedImoNumber(null);
  }, [reset, resetAttachmentUpload]);

  useEffect(() => {
    resetAttachmentUpload();

    return () => {
      resetAttachmentUpload();
    };
  }, [resetAttachmentUpload]);

  useEffect(() => {
    const trimmedImoNumber = currentImoNumber.trim();

    if (!trimmedImoNumber) {
      if (resolvedImoNumber !== null) {
        resetRegisteredVesselFields();
        setResolvedImoNumber(null);
      }
      return;
    }

    if (resolvedImoNumber && trimmedImoNumber !== resolvedImoNumber) {
      resetRegisteredVesselFields();
      setResolvedImoNumber(null);
    }
  }, [currentImoNumber, resetRegisteredVesselFields, resolvedImoNumber]);

  const goToDeficienciesList = useCallback(() => {
    navigate(`/${paths.deficiencies.index}`);
  }, [navigate]);

  const lookupRegisteredVesselByImoNumber = useCallback(
    async (rawImoNumber: string) => {
      const trimmedImoNumber = rawImoNumber.trim();
      const hasValidImoNumber = new RegExp(`^\\d{1,${MAX_DEFICIENCY_IMO_DIGITS}}$`).test(
        trimmedImoNumber,
      );

      if (!trimmedImoNumber || !hasValidImoNumber) {
        return false;
      }

      const requestId = lookupRequestIdRef.current + 1;
      lookupRequestIdRef.current = requestId;
      setIsLookingUpVessel(true);
      clearErrors(F.IMO_NO);

      try {
        const response = await getSearchVessels(VESSEL_SERVICE, { page: 1, pageSize: 1 }, [
          { field: "imoNumbers", value: trimmedImoNumber },
        ]);

        if (lookupRequestIdRef.current !== requestId) {
          return false;
        }

        const registeredVessel = response.vessels.find((vessel) =>
          isMatchingImoNumber(vessel, trimmedImoNumber),
        );

        if (!registeredVessel) {
          resetRegisteredVesselFields();
          setResolvedImoNumber(null);
          setError(F.IMO_NO, {
            type: "manual",
            message: t("deficiencies.imo-not-found"),
          });

          return false;
        }

        const mappedValues = mapRegisteredVesselToDeficiencyFormValues(registeredVessel, companies);

        setValue(F.REGISTERED_VESSEL_ID, registeredVessel.id, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
        setValue(F.VESSEL_NAME, mappedValues.vesselName, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
        setValue(F.SHIPPING_AGENCY, mappedValues.shippingAgency, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
        setValue(F.LOA, mappedValues.loa, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });

        setResolvedImoNumber(trimmedImoNumber);
        clearErrors(F.IMO_NO);

        return true;
      } catch (error) {
        console.error("Unable to fetch vessel details by IMO number:", error);

        if (lookupRequestIdRef.current !== requestId) {
          return false;
        }

        resetRegisteredVesselFields();
        setResolvedImoNumber(null);

        return false;
      } finally {
        if (lookupRequestIdRef.current === requestId) {
          setIsLookingUpVessel(false);
        }
      }
    },
    [clearErrors, companies, resetRegisteredVesselFields, setError, setValue, t],
  );

  const handleRemoveAttachment = useCallback(
    (index: number) => {
      setAttachmentFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    },
    [setAttachmentFiles],
  );

  const handleCancel = () => {
    clearDeficiencyForm();
    goToDeficienciesList();
  };

  const onSubmit = useCallback(async () => {
    const latestFormValues = getValues();
    const wasSubmitted = await submitRequest(latestFormValues);

    if (!wasSubmitted) {
      return;
    }

    clearDeficiencyForm();
    goToDeficienciesList();
  }, [clearDeficiencyForm, getValues, goToDeficienciesList, submitRequest]);

  const onInvalid = useCallback(() => {
    notify({
      title: t("deficiencies.notifications.submit-error-title"),
      description: t("deficiencies.notifications.submit-error-description"),
      type: "error",
    });
  }, [notify, t]);

  const handleFormSubmit = useCallback(async () => {
    const trimmedImoNumber = String(getValues(F.IMO_NO) ?? "").trim();
    const registeredVesselId = String(getValues(F.REGISTERED_VESSEL_ID) ?? "").trim();
    const shouldLookup =
      trimmedImoNumber.length > 0 &&
      (trimmedImoNumber !== String(resolvedImoNumber ?? "") || !registeredVesselId);

    if (shouldLookup) {
      const wasResolved = await lookupRegisteredVesselByImoNumber(trimmedImoNumber);

      if (!wasResolved) {
        onInvalid();
        return;
      }
    }

    await form.handleSubmit(onSubmit, onInvalid)();
  }, [form, getValues, lookupRegisteredVesselByImoNumber, onInvalid, onSubmit, resolvedImoNumber]);

  if (!canRequestFottDeficiency) {
    return null;
  }

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId={`${TEST_ID_BASE}-subheader`}
        subtitle={t(`${LANG_BASE}.subtitle`)}
        title={t(`${LANG_BASE}.add-deficiency`)}
      />

      <div className="px-24 py-8">
        <div className="mx-auto max-w-[760px] flex flex-col gap-6">
          <FormProvider {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void handleFormSubmit();
              }}
              className="flex flex-col gap-6"
            >
              <FormSection title={t(`${LANG_BASE}.form-section-title`)}>
                <div
                  onBlur={(event) => {
                    const nextFocusedElement = event.relatedTarget as Node | null;
                    if (nextFocusedElement && event.currentTarget.contains(nextFocusedElement)) {
                      return;
                    }

                    void lookupRegisteredVesselByImoNumber(String(getValues(F.IMO_NO) ?? ""));
                  }}
                >
                  <FormItem<CreateDeficiencyFormData, typeof F.IMO_NO>
                    required
                    name={F.IMO_NO}
                    type="number"
                    maxDigits={MAX_DEFICIENCY_IMO_DIGITS}
                    label={t(`${LANG_BASE}.headerLabels.imoNo`)}
                    testId={`${TEST_ID_BASE}-imo-no`}
                    onFieldChange={() => {
                      clearErrors(F.IMO_NO);
                    }}
                  />
                </div>
                <FormItem<CreateDeficiencyFormData, typeof F.VESSEL_NAME>
                  name={F.VESSEL_NAME}
                  type="text"
                  maxChars={150}
                  label={t(`${LANG_BASE}.headerLabels.vesselName`)}
                  testId={`${TEST_ID_BASE}-vessel-name`}
                  disabled
                />
                <FormItem<CreateDeficiencyFormData, typeof F.TRIP_NO_PLDA>
                  required
                  name={F.TRIP_NO_PLDA}
                  type="text"
                  maxChars={MAX_TRIP_NO_PLDA_LENGTH}
                  label={t(`${LANG_BASE}.headerLabels.tripNoPlda`)}
                  testId={`${TEST_ID_BASE}-trip-no-plda`}
                />
                <FormItem<CreateDeficiencyFormData, typeof F.SHIPPING_AGENCY>
                  name={F.SHIPPING_AGENCY}
                  type="text"
                  maxChars={150}
                  label={t(`${LANG_BASE}.headerLabels.shippingAgency`)}
                  testId={`${TEST_ID_BASE}-shipping-agency`}
                  disabled
                />
                <FormItem<CreateDeficiencyFormData, typeof F.BERTH_VISITED>
                  required
                  name={F.BERTH_VISITED}
                  type="select"
                  options={berthVisitedOptions}
                  label={t(`${LANG_BASE}.headerLabels.berthVisited`)}
                  testId={`${TEST_ID_BASE}-berth-visited`}
                  disabled={isLoadingBerthVisitedCodes}
                />
                <FormItem<CreateDeficiencyFormData, typeof F.LOA>
                  name={F.LOA}
                  type="text"
                  maxChars={20}
                  label={t(`${LANG_BASE}.headerLabels.loa`)}
                  testId={`${TEST_ID_BASE}-loa`}
                  disabled
                />
                <div className="md:col-span-full">
                  <FormItem<CreateDeficiencyFormData, typeof F.DEFICIENCY_DETAILS>
                    required
                    name={F.DEFICIENCY_DETAILS}
                    type="textarea"
                    minRows={5}
                    maxChars={MAX_DEFICIENCY_DETAILS_LENGTH}
                    showCharCounter
                    label={t(`${LANG_BASE}.headerLabels.deficiencyDetails`)}
                    testId={`${TEST_ID_BASE}-deficiency-details`}
                  />
                </div>
                <div className="md:col-span-full">
                  <FormItem<CreateDeficiencyFormData, typeof F.REMARKS>
                    name={F.REMARKS}
                    type="textarea"
                    minRows={3}
                    maxChars={MAX_DEFICIENCY_REMARKS_LENGTH}
                    showCharCounter
                    label={t(`${LANG_BASE}.headerLabels.remarks`)}
                    testId={`${TEST_ID_BASE}-remarks`}
                  />
                </div>
                <div className="md:col-span-full flex flex-col gap-4">
                  <FormItem<CreateDeficiencyFormData, typeof F.ATTACHMENTS>
                    name={F.ATTACHMENTS}
                    type="file"
                    maxFiles={MAX_DEFICIENCY_ATTACHMENTS}
                    label={t(`${LANG_BASE}.attachments-label`)}
                    testId={`${TEST_ID_BASE}-attachments`}
                    uploadConditions={attachmentUploadConditions}
                    allowedFileExtensions={allowedAttachmentExtensions}
                    uploadDescription={t("upload.additional-documents-upload", {
                      extensions: attachmentUploadConditions.format?.join(", "),
                      size: attachmentUploadConditions.maxSizeLabel,
                    })}
                    files={attachmentFiles}
                    setFiles={setAttachmentFiles}
                    onUpload={(_, files) => handleAttachmentUpload(files)}
                    disabled={isSubmitting || isUploadingAttachments}
                  />

                  {attachmentFiles.length > 0 && (
                    <div className="flex flex-col gap-4">
                      {attachmentFiles.map((file, index) => (
                        <SingleFile
                          key={`${file.name}-${file.lastModified ?? index}`}
                          testId={`${TEST_ID_BASE}-attachment-${index}`}
                          file={file}
                          fieldName={F.ATTACHMENTS}
                          currentFileIndex={index}
                          setFiles={() => handleRemoveAttachment(index)}
                          isLoading={isUploadingAttachments && file instanceof File}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </FormSection>

              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  testId={`${TEST_ID_BASE}-cancel-button`}
                  label={t("common.cancel")}
                  sx={{ textTransform: "none", px: 0 }}
                  onClick={handleCancel}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || isLookingUpVessel || isUploadingAttachments}
                  testId={`${TEST_ID_BASE}-submit-button`}
                  label={t("common.submit")}
                  sx={{ textTransform: "none", height: "40px", minHeight: "40px" }}
                />
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </PageLayout>
  );
};

export default CreateDeficiencyPage;
